/**
 * Personal connect codes (TNG-93): eight characters of Crockford base32.
 *
 * The backend mints and stores them (connection.py); this file reads what a
 * person typed, scanned or opened as a link, in the same spelling the rules
 * accept on a CONNECT request (`^[0-9A-HJKMNP-TV-Z]{8}$`).
 */

/** Crockford base32: no I, L, O or U, so nothing on screen reads as another. */
export const CODE_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
export const CODE_LENGTH = 8;

const READ_AS: Record<string, string> = { O: "0", I: "1", L: "1" };

/**
 * The stored spelling of a code, or null.
 *
 * Case, spaces and dashes are ignored and O, I and L read as the digits they
 * look like, as Crockford's decoding does. The backend applies the same rules
 * (`normalize_code`), so what passes here passes there.
 */
export function normalizeCode(input: string): string | null {
  const cleaned = Array.from(input.toUpperCase())
    .filter((character) => character !== " " && character !== "-")
    .map((character) => READ_AS[character] ?? character)
    .join("");
  if (cleaned.length !== CODE_LENGTH) return null;
  for (const character of cleaned) {
    if (!CODE_ALPHABET.includes(character)) return null;
  }
  return cleaned;
}

/** K7P4QX9M -> K7P4-QX9M, the way it is read aloud. */
export function formatCode(code: string): string {
  return `${code.slice(0, 4)}-${code.slice(4)}`;
}

/**
 * What the QR on the code sheet encodes: a link into this app, so a phone's
 * own camera opens the connect page, and the in-app scanner reads the code
 * out of it. `e` is the slug of the event the code was shown in.
 */
export function connectUrl(
  origin: string,
  code: string,
  slug?: string,
): string {
  const url = new URL(`/connect/${code}`, origin);
  if (slug) url.searchParams.set("e", slug);
  return url.toString();
}

export interface ScannedCode {
  code: string;
  /** The event slug the link named, when it was a link. */
  slug?: string;
}

/**
 * A code out of whatever the camera or a photo decoded: one of our connect
 * links, or the bare code. A link is recognised by its path alone, so a QR
 * made on a preview or local host still reads; the code it carries is only a
 * code, which the backend resolves or refuses. Anything else is not a code.
 */
export function parseScanned(text: string): ScannedCode | null {
  const trimmed = text.trim();
  if (/^https?:\/\//i.test(trimmed)) {
    let url: URL;
    try {
      url = new URL(trimmed);
    } catch {
      return null;
    }
    const match = /^\/connect\/([^/]+)\/?$/.exec(url.pathname);
    if (!match) return null;
    const code = normalizeCode(decodeURIComponent(match[1]));
    if (!code) return null;
    const slug = url.searchParams.get("e") ?? undefined;
    return slug ? { code, slug } : { code };
  }
  const code = normalizeCode(trimmed);
  return code ? { code } : null;
}
